import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bookmark, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrueReturnSaves({ user, tickers, years, onLoad, onLoginRequired }) {
  const [saves, setSaves] = useState([]);
  const [max, setMax] = useState(5);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) { setSaves([]); return; }
    try {
      const r = await axios.get(`${API}/true-return/saves`, { withCredentials: true });
      setSaves(r.data.saves || []);
      setMax(r.data.max || 5);
    } catch { setSaves([]); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const sameSet = (a, b) => [...a].sort().join(',') === [...b].sort().join(',');
  const alreadySaved = saves.some((s) => sameSet(s.tickers, tickers));

  const save = async () => {
    if (!user) { onLoginRequired(); return; }
    if (tickers.length < 2 || alreadySaved || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/true-return/saves`, { tickers, years }, { withCredentials: true });
      if (r.data.duplicate) toast.info('That combination is already saved');
      else { toast.success('Chart saved'); await refresh(); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/true-return/saves/${id}`, { withCredentials: true });
      setSaves((s) => s.filter((x) => x.save_id !== id));
      await refresh();
    } catch { toast.error('Could not remove'); }
  };

  return (
    <div className="rounded-xl border bg-card px-3 py-2.5" data-testid="true-return-saves">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={tickers.length < 2 || busy || (user && (alreadySaved || saves.length >= max))}
          className="inline-flex items-center gap-1.5 rounded-full bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title={!user ? 'Sign in to save charts' : alreadySaved ? 'Already saved' : saves.length >= max ? `Maximum ${max} saved charts` : 'Save this combination'}
          data-testid="true-return-save-btn"
        >
          <Bookmark className="w-3.5 h-3.5" />
          {alreadySaved ? 'Saved' : 'Save this chart'}
        </button>
        <span className="text-[11px] text-muted-foreground">
          {user ? `${saves.length}/${max} saved` : 'Sign in (Google or guest) to keep up to 5 combinations'}
        </span>
      </div>
      {saves.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="true-return-saves-list">
          {saves.map((s) => {
            const active = sameSet(s.tickers, tickers);
            return (
              <span key={s.save_id} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs ${active ? 'border-sky-500 bg-sky-500/10' : 'bg-muted/40'}`} data-testid={`true-return-save-${s.save_id}`}>
                <button type="button" onClick={() => onLoad(s.tickers, s.years)} className="font-mono font-semibold hover:underline" data-testid={`true-return-save-load-${s.save_id}`}>
                  {s.tickers.join(' vs ')}
                </button>
                <span className="text-muted-foreground">{s.years}y</span>
                <button type="button" onClick={() => remove(s.save_id)} className="text-muted-foreground hover:text-foreground" aria-label="Remove saved chart" data-testid={`true-return-save-remove-${s.save_id}`}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
